(() => {
  'use strict';

  const API = 'https://app.btime.om';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const isArabic = () => document.documentElement.lang?.toLowerCase().startsWith('ar');
  const translate = (arabic, english) => isArabic() ? arabic : english;
  const normalizeId = (value) => String(value || '').replace(/\D/g, '');

  async function request(url, options = {}, timeout = 9000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timer);
    }
  }

  function escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = String(value || '');
    return element.innerHTML;
  }

  function photoUrl(photo) {
    const value = typeof photo === 'string' ? photo : photo?.url;
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  }

  function reviewPhotos(review) {
    let photos = review?.photos || [];
    if (typeof photos === 'string') {
      try { photos = JSON.parse(photos); } catch (_) { photos = []; }
    }
    return Array.isArray(photos) ? photos.map(photoUrl).filter(Boolean).slice(0, 4) : [];
  }

  function createLightbox() {
    let lightbox = $('[data-bt-review-lightbox]');
    if (lightbox) return lightbox;

    lightbox = document.createElement('div');
    lightbox.className = 'bt-review-lightbox';
    lightbox.dataset.btReviewLightbox = '';
    lightbox.hidden = true;
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', translate('صورة التقييم', 'Review photo'));
    lightbox.innerHTML = `
      <button type="button" class="bt-review-lightbox__backdrop" data-bt-review-close tabindex="-1" aria-label="${translate('إغلاق الصورة', 'Close photo')}"></button>
      <div class="bt-review-lightbox__panel">
        <button type="button" class="bt-review-lightbox__close" data-bt-review-close aria-label="${translate('إغلاق', 'Close')}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
        </button>
        <button type="button" class="bt-review-lightbox__nav bt-review-lightbox__nav--previous" data-bt-review-previous aria-label="${translate('الصورة السابقة', 'Previous photo')}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.5 5-7 7 7 7"/></svg>
        </button>
        <figure>
          <img data-bt-review-lightbox-image alt="" decoding="async">
          <figcaption data-bt-review-lightbox-count></figcaption>
        </figure>
        <button type="button" class="bt-review-lightbox__nav bt-review-lightbox__nav--next" data-bt-review-next aria-label="${translate('الصورة التالية', 'Next photo')}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 5 7 7-7 7"/></svg>
        </button>
      </div>`;
    document.body.appendChild(lightbox);
    return lightbox;
  }

  function lightboxController(root) {
    const lightbox = createLightbox();
    const image = $('[data-bt-review-lightbox-image]', lightbox);
    const count = $('[data-bt-review-lightbox-count]', lightbox);
    const previous = $('[data-bt-review-previous]', lightbox);
    const next = $('[data-bt-review-next]', lightbox);
    let gallery = [];
    let index = 0;
    let returnFocus = null;
    let pointerStartX = null;

    function render() {
      const item = gallery[index];
      if (!item) return;
      image.src = item.src;
      image.alt = item.alt;
      count.textContent = gallery.length > 1 ? `${index + 1} / ${gallery.length}` : '';
      previous.hidden = gallery.length < 2;
      next.hidden = gallery.length < 2;
    }

    function open(button) {
      const review = button.closest('.bt-review');
      gallery = $$('[data-bt-review-photo]', review).map((item) => ({
        src: item.dataset.src,
        alt: item.dataset.alt || translate('صورة مرفقة مع التقييم', 'Photo attached to the review')
      })).filter((item) => item.src);
      index = Math.max(0, $$('[data-bt-review-photo]', review).indexOf(button));
      returnFocus = button;
      render();
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bt-review-lightbox-open');
      $('[data-bt-review-close]:not(.bt-review-lightbox__backdrop)', lightbox)?.focus({ preventScroll: true });
    }

    function close() {
      if (lightbox.hidden) return;
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bt-review-lightbox-open');
      image.removeAttribute('src');
      returnFocus?.focus?.({ preventScroll: true });
      gallery = [];
    }

    function move(amount) {
      if (gallery.length < 2) return;
      index = (index + amount + gallery.length) % gallery.length;
      render();
    }

    root.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-bt-review-photo]');
      if (button) open(button);
    });
    lightbox.addEventListener('click', (event) => {
      if (event.target.closest('[data-bt-review-close]')) close();
      else if (event.target.closest('[data-bt-review-previous]')) move(-1);
      else if (event.target.closest('[data-bt-review-next]')) move(1);
    });
    lightbox.addEventListener('pointerdown', (event) => { pointerStartX = event.clientX; }, { passive: true });
    lightbox.addEventListener('pointerup', (event) => {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) > 45) move(distance > 0 ? -1 : 1);
    }, { passive: true });
    document.addEventListener('keydown', (event) => {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowLeft') move(isArabic() ? 1 : -1);
      else if (event.key === 'ArrowRight') move(isArabic() ? -1 : 1);
    });
  }

  function initialize() {
    const root = $('[data-bt-product-reviews]');
    if (!root || root.dataset.btReviewsReady === '1') return;
    root.dataset.btReviewsReady = '1';

    const descriptionAccordion = $('.hdt-section-main-product-tabs .hdt-product-accordion__content.hdt-rte')?.closest('hdt-accordion');
    const tabsSection = $('.hdt-section-main-product-tabs');
    if (descriptionAccordion) descriptionAccordion.insertAdjacentElement('afterend', root);
    else if (tabsSection) tabsSection.insertAdjacentElement('afterend', root);

    const id = normalizeId(root.dataset.productId);
    const list = $('[data-list]', root);
    let selectedRating = 5;
    let loadingTimer = 0;
    let previewUrls = [];

    const productTitle = $('.hdt-product__title');
    if (productTitle && !$('.bt-product-rating-link')) {
      const link = document.createElement('a');
      link.className = 'bt-product-rating-link';
      link.href = '#bt-product-reviews';
      link.innerHTML = `<span class="bt-rating-badge is-loading" data-bt-rating data-product-id="${id}" data-rating="0" data-count="0"><span class="bt-stars"><span class="bt-stars-fill" style="width:0%"></span></span><span data-bt-count></span></span>`;
      productTitle.insertAdjacentElement('afterend', link);
      window.BTReviewBadges?.load(link);
    }

    const picker = $('[data-bt-picker]', root);
    if (picker) {
      $$('button', picker).forEach((button) => {
        button.addEventListener('click', () => {
          selectedRating = Number(button.dataset.value) || 5;
          $$('button', picker).forEach((item) => item.classList.toggle('on', Number(item.dataset.value) <= selectedRating));
          const input = $('[name="rating"]', root);
          if (input) input.value = selectedRating;
        });
      });
    }

    function reviewMarkup(review) {
      const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
      const date = review.createdAt
        ? new Intl.DateTimeFormat(isArabic() ? 'ar-OM' : 'en-OM').format(new Date(Number(review.createdAt)))
        : '';
      const photos = reviewPhotos(review);
      const photosMarkup = photos.length
        ? `<div class="bt-review-photos" aria-label="${translate('صور التقييم', 'Review photos')}">${photos.map((src, photoIndex) => {
            const alt = translate(`صورة التقييم ${photoIndex + 1}`, `Review photo ${photoIndex + 1}`);
            return `<button type="button" class="bt-review-photo" data-bt-review-photo data-src="${escapeHtml(src)}" data-alt="${alt}" aria-label="${translate('فتح صورة التقييم', 'Open review photo')}"><img src="${escapeHtml(src)}" alt="${alt}" width="112" height="112" loading="lazy" decoding="async"></button>`;
          }).join('')}</div>`
        : '';

      return `<article class="bt-review"><div class="bt-review-meta"><div><div class="bt-review-author">${escapeHtml(review.author || translate('عميل بيوتي تايم', 'Beauty Time customer'))}</div><span class="bt-stars" aria-label="${rating} ${translate('من 5', 'out of 5')}"><span class="bt-stars-fill" style="width:${rating * 20}%"></span></span></div><time class="bt-review-date">${escapeHtml(date)}</time></div>${review.comment ? `<p>${escapeHtml(review.comment)}</p>` : ''}${photosMarkup}</article>`;
    }

    function updateSummary(data) {
      const average = Number(data.average || data.rating) || 0;
      const total = Number(data.count || data.total) || 0;
      const score = $('[data-score]', root);
      if (score) score.textContent = average.toFixed(1);
      window.BTReviewBadges?.render($('[data-summary]', root), average, total);
      window.BTReviewBadges?.render($('.bt-product-rating-link .bt-rating-badge'), average, total);

      for (let rating = 1; rating <= 5; rating += 1) {
        const amount = Number(data[`rating${rating}`] || data.counts?.[rating]) || 0;
        const row = $(`[data-bar="${rating}"]`, root);
        if (!row) continue;
        $('.bt-fill', row)?.style.setProperty('width', `${total ? amount / total * 100 : 0}%`);
        const number = $('b', row);
        if (number) number.textContent = amount;
      }
    }

    function showDelayedLoader() {
      window.clearTimeout(loadingTimer);
      loadingTimer = window.setTimeout(() => {
        if (!list) return;
        list.setAttribute('aria-busy', 'true');
        list.innerHTML = `<span class="bt-review-loading-label">${translate('نجهّز التقييمات…', 'Preparing reviews…')}</span>`;
      }, 180);
    }

    async function loadReviews() {
      showDelayedLoader();
      try {
        const response = await request(`${API}/api/bt-reviews?productId=${id}&page=1&limit=50&lang=${isArabic() ? 'ar' : 'en'}`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`HTTP_${response.status}`);
        const data = await response.json();
        const reviews = data.reviews || data.data || [];
        const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        reviews.forEach((review) => {
          const rating = Math.max(1, Math.min(5, Math.round(Number(review.rating) || 0)));
          counts[rating] += 1;
        });
        updateSummary({ ...data, ...(data.summary || {}), counts });
        list.innerHTML = reviews.length
          ? reviews.map(reviewMarkup).join('')
          : `<div class="bt-empty">${translate('لا توجد تقييمات بعد.', 'No reviews yet.')}</div>`;
      } catch (_) {
        list.innerHTML = `<div class="bt-empty">${translate('تعذر تحميل التقييمات الآن.', 'Reviews are unavailable right now.')}</div>`;
      } finally {
        window.clearTimeout(loadingTimer);
        list?.removeAttribute('aria-busy');
      }
    }

    const form = $('form', root);
    if (form) {
      const files = $('[data-bt-review-files]', form);
      const preview = $('[data-bt-image-preview]', form);
      const status = $('[data-status]', form);
      const submit = $('[type="submit"]', form);

      function clearPreviews() {
        previewUrls.forEach((url) => URL.revokeObjectURL(url));
        previewUrls = [];
        if (preview) preview.replaceChildren();
      }

      files?.addEventListener('change', () => {
        clearPreviews();
        Array.from(files.files || []).slice(0, 4).forEach((file) => {
          const url = URL.createObjectURL(file);
          previewUrls.push(url);
          const item = document.createElement('span');
          item.className = 'bt-upload-preview';
          const image = document.createElement('img');
          image.src = url;
          image.alt = translate('معاينة صورة التقييم', 'Review photo preview');
          image.width = 58;
          image.height = 58;
          const check = document.createElement('b');
          check.textContent = '✓';
          item.append(image, check);
          preview?.appendChild(item);
        });
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        status.textContent = translate('جاري الإرسال…', 'Submitting…');
        try {
          const data = new FormData(form);
          data.set('productId', id);
          data.set('productName', root.dataset.productTitle || '');
          data.set('lang', isArabic() ? 'ar' : 'en');
          const response = await request(`${API}/api/bt-reviews?lang=${isArabic() ? 'ar' : 'en'}`, { method: 'POST', body: data }, 15000);
          if (!response.ok) throw new Error(`HTTP_${response.status}`);
          form.reset();
          selectedRating = 5;
          if (picker) $$('button', picker).forEach((button) => button.classList.add('on'));
          clearPreviews();
          status.textContent = translate('شكرًا، أُرسل تقييمك للمراجعة.', 'Thank you. Your review was submitted.');
          await loadReviews();
        } catch (_) {
          status.textContent = translate('تعذر إرسال التقييم.', 'Could not submit the review.');
        } finally {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
      });
    }

    lightboxController(root);
    loadReviews();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
